import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, useTheme, Typography, IconButton, Box, Select, MenuItem, InputLabel, FormControl, OutlinedInput, Grid, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import { useGetDataAssetByNameQuery } from "../../../../features/data_assets/dataAssetsApiSlice";
import { useGetOrganizationsQuery } from '../../../../features/organizations/organizationsApiSlice';
import { InfoOutlined } from '@mui/icons-material';
import Header from '../../global/Header';
import { tokens } from '../../../../theme';
import { useDiscreteMode } from '../../global/DiscreteMode';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const CountSummaries = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [dateRange, setDateRange] = useState('7d');
  const [infoOpen, setInfoOpen] = useState(false);
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  
  // Single declaration for orgSearch state for filtering organizations
  const [orgSearch, setOrgSearch] = useState('');

  const { data: organizations, isLoading, isError } = useGetOrganizationsQuery('organizations', {});
  const orgArray = Object.values(organizations?.entities || {});

  const numberFormat = new Intl.NumberFormat();
  const [selectedStat, setSelectedStat] = useState('visits_count');
  const [selectedChartOrgs, setSelectedChartOrgs] = useState([]);
  const { isDiscreteMode } = useDiscreteMode();

  const columns = [
    {
      field: 'member_name',
      headerName: 'Member Name',
      flex: 1,
      renderCell: (params) =>
        isDiscreteMode ? <Box width="100px" height="20px" bgcolor="white" /> : <span>{params.value}</span>,
    },
    { field: 'visits_count', headerName: 'Total Visits', flex: 1, valueFormatter: ({ value }) => numberFormat.format(value) },
    { field: 'visits_resolved_count', headerName: 'Total Visits Resolved', flex: 1, valueFormatter: ({ value }) => numberFormat.format(value) },
    { field: 'distinct_visits_count', headerName: 'Total Distinct Visits', flex: 1, valueFormatter: ({ value }) => numberFormat.format(value) },
    { field: 'distinct_visits_resolved_count', headerName: 'Total Distinct Visits Resolved', flex: 1, valueFormatter: ({ value }) => numberFormat.format(value) },
    { field: 'distinct_hems_count', headerName: 'Total Distinct HEMs Delivered', flex: 1, valueFormatter: ({ value }) => numberFormat.format(value) },
    { field: 'percent_resolved', headerName: 'Total % Resolved', flex: 1, valueFormatter: ({ value }) => `${(value * 100).toFixed(2)}%` },
    { field: 'unique_percent_resolved', headerName: 'Total Unique % Resolved', flex: 1, valueFormatter: ({ value }) => `${(value * 100).toFixed(2)}%` },
    { field: 'date_range', headerName: 'Date Range', flex: 1, valueGetter: ({ value }) => value },
  ];

  const organizationsData = useMemo(() => {
    if (!organizations?.entities) {
      console.log("Organizations data is not available yet.");
      return [];
    }
    return Object.values(organizations.entities).flatMap(organization => {
      if (!organization.adc_enabled || !organization.adc_counts) return [];
      return organization.adc_counts
        .filter(count => count.on_date !== 'summary')
        .map((count, index) => ({
          id: `${organization.id}-${index}`,
          ...count,
          member_name: organization.name,
          percent_resolved: count.visits_resolved_count / count.visits_count,
          unique_percent_resolved: count.distinct_visits_resolved_count / count.distinct_visits_count,
        }));
    });
  }, [organizations?.entities]);

  const filteredData = useMemo(() => {
    const today = new Date();
    let startDate, endDate = today;
    switch (dateRange) {
      case '30d':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        break;
      case '7d':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        break;
      case '365d':
        startDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
        break;
      case 'custom':
        startDate = customRange.start ? new Date(customRange.start) : new Date(0);
        endDate = customRange.end ? new Date(customRange.end) : today;
        break;
      default:
        startDate = new Date(0);
    }
    const filteredRecords = organizationsData.filter(item => {
      const itemDate = new Date(item.on_date);
      if (dateRange === 'custom') {
        return itemDate >= startDate && itemDate <= endDate;
      }
      return itemDate >= startDate;
    });
    const summary = filteredRecords.reduce((acc, item) => {
      if (!acc[item.member_name]) {
        acc[item.member_name] = {
          id: `summary-${item.member_name}`,
          member_name: item.member_name,
          visits_count: 0,
          visits_resolved_count: 0,
          distinct_visits_count: 0,
          distinct_visits_resolved_count: 0,
          distinct_hems_count: 0,
          date_range: '',
        };
      }
      acc[item.member_name].visits_count += item.visits_count;
      acc[item.member_name].visits_resolved_count += item.visits_resolved_count;
      acc[item.member_name].distinct_visits_count += item.distinct_visits_count;
      acc[item.member_name].distinct_visits_resolved_count += item.distinct_visits_resolved_count;
      acc[item.member_name].distinct_hems_count += item.distinct_hems_count;
      const itemDate = new Date(item.on_date);
      if (!acc[item.member_name].startDate || itemDate < acc[item.member_name].startDate) {
        acc[item.member_name].startDate = itemDate;
      }
      if (!acc[item.member_name].endDate || itemDate > acc[item.member_name].endDate) {
        acc[item.member_name].endDate = itemDate;
      }
      return acc;
    }, {});
    Object.values(summary).forEach(orgSummary => {
      if (orgSummary.startDate && orgSummary.endDate) {
        orgSummary.percent_resolved = orgSummary.visits_count > 0
          ? (orgSummary.visits_resolved_count / orgSummary.visits_count)
          : 0;
        orgSummary.unique_percent_resolved = orgSummary.distinct_visits_count > 0
          ? (orgSummary.distinct_visits_resolved_count / orgSummary.distinct_visits_count)
          : 0;
        orgSummary.date_range = `${orgSummary.startDate.toISOString().split('T')[0]} -> ${orgSummary.endDate.toISOString().split('T')[0]}`;
      }
      delete orgSummary.startDate;
      delete orgSummary.endDate;
    });
    return Object.values(summary);
  }, [organizationsData, dateRange, customRange]);

  const aggregateStats = useMemo(() => {
    let totalVisits = 0,
      totalResolved = 0,
      totalDistinctVisits = 0,
      totalDistinctResolved = 0;
    let sumPercentResolved = 0,
      sumUniquePercentResolved = 0;
    let countValid = 0;
    filteredData.forEach(item => {
      if (item.visits_count > 0) {
        totalVisits += item.visits_count;
        totalResolved += item.visits_resolved_count;
        totalDistinctVisits += item.distinct_visits_count;
        totalDistinctResolved += item.distinct_visits_resolved_count;
        const pr = item.visits_resolved_count / item.visits_count;
        sumPercentResolved += pr;
        const upr = item.distinct_visits_resolved_count / item.distinct_visits_count;
        sumUniquePercentResolved += upr;
        countValid++;
      }
    });
    const avgPercentResolved = countValid > 0 ? (sumPercentResolved / countValid) : 0;
    const avgUniquePercentResolved = countValid > 0 ? (sumUniquePercentResolved / countValid) : 0;
    const overallResolutionRate = totalVisits > 0 ? (totalResolved / totalVisits) : 0;
    const overallDistinctResolutionRate = totalDistinctVisits > 0 ? (totalDistinctResolved / totalDistinctVisits) : 0;
    return {
      totalVisits,
      totalResolved,
      totalDistinctVisits,
      totalDistinctResolved,
      avgPercentResolved,
      avgUniquePercentResolved,
      overallResolutionRate,
      overallDistinctResolutionRate,
    };
  }, [filteredData]);

  // Export functions
  const handleExportCSV = () => {
    if (filteredData.length === 0) return;
    const headers = Object.keys(filteredData[0]);
    const csvRows = [];
    csvRows.push(headers.join(","));
    filteredData.forEach(row => {
      const values = headers.map(header => {
        const val = row[header];
        if (typeof val === "string") {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      });
      csvRows.push(values.join(","));
    });
    const csvContent = csvRows.join("\n");
    const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "adc_stats.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportJSON = () => {
    const jsonContent = JSON.stringify(filteredData, null, 2);
    const blob = new Blob([jsonContent], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "adc_stats.json");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSelectAllOrgs = () => {
    const allOrgNames = orgArray.filter(org => org.adc_enabled).map(org => org.name);
    setSelectedChartOrgs(allOrgNames);
  };

  const handleDeselectAllOrgs = () => {
    setSelectedChartOrgs([]);
  };

  const handleDateRangeChange = (event) => {
    const value = event.target.value;
    setDateRange(value);
  };

  const handleStatChange = (event) => {
    setSelectedStat(event.target.value);
  };

  const handleChartOrgChange = (event) => {
    setSelectedChartOrgs(event.target.value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{`Date: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.stroke }}>{`${entry.name}: ${entry.value}`}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  const chartData = useMemo(() => {
    if (!organizationsData || selectedChartOrgs.length === 0) return [];
    let startDate, endDate;
    const today = new Date();
    switch (dateRange) {
      case '7d':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        endDate = today;
        break;
      case '30d':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        endDate = today;
        break;
      case '365d':
        startDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
        endDate = today;
        break;
      case 'custom':
        startDate = customRange.start ? new Date(customRange.start) : new Date(0);
        endDate = customRange.end ? new Date(customRange.end) : today;
        break;
      default:
        startDate = new Date(0);
        endDate = today;
    }
    const dataByDate = {};
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const formattedDate = d.toISOString().split('T')[0];
      dataByDate[formattedDate] = {};
      selectedChartOrgs.forEach(org => {
        dataByDate[formattedDate][org] = null;
      });
    }
    organizationsData.forEach(item => {
      const itemDate = item.on_date;
      if (itemDate in dataByDate && selectedChartOrgs.includes(item.member_name)) {
        const statValue = (selectedStat in item && typeof item[selectedStat] === 'number') ? item[selectedStat] : 0;
        dataByDate[itemDate][item.member_name] = statValue;
      }
    });
    return Object.keys(dataByDate).map(date => {
      const formattedData = { date };
      selectedChartOrgs.forEach(org => {
        if (dataByDate[date][org] !== null) {
          if (selectedStat === 'percent_resolved' || selectedStat === 'unique_percent_resolved') {
            formattedData[org] = (dataByDate[date][org] * 100).toFixed(2);
          } else {
            formattedData[org] = dataByDate[date][org];
          }
        } else {
          formattedData[org] = null;
        }
      });
      return formattedData;
    });
  }, [organizationsData, selectedChartOrgs, selectedStat, dateRange, customRange]);

  // Only set a random org when none is selected
  useEffect(() => {
    if (orgArray.length > 0 && selectedChartOrgs.length === 0) {
      const adcEnabledOrgs = orgArray.filter(org => org.adc_enabled).map(org => org.name);
      const randomOrg = adcEnabledOrgs[Math.floor(Math.random() * adcEnabledOrgs.length)];
      setSelectedChartOrgs([randomOrg]);
    }
  }, [orgArray, selectedChartOrgs]);

  // When user selects "custom", if no custom dates are set, default to last 7 days
  useEffect(() => {
    if (dateRange === 'custom' && (!customRange.start || !customRange.end)) {
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      setCustomRange({
        start: sevenDaysAgo.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      });
    }
  }, [dateRange, customRange.start, customRange.end]);

  const getRandomColor = () => {
    const letters = '89ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * letters.length)];
    }
    return color;
  };

  const [orgColors, setOrgColors] = useState({});
  const prevOrgsRef = useRef();
  useEffect(() => {
    const hasOrgsChanged = JSON.stringify(orgArray) !== JSON.stringify(prevOrgsRef.current);
    if (hasOrgsChanged) {
      prevOrgsRef.current = orgArray;
      const newOrgColors = orgArray.filter(org => org.adc_enabled)
        .reduce((acc, org) => {
          acc[org.name] = getRandomColor();
          return acc;
        }, {});
      setOrgColors(newOrgColors);
    }
  }, [orgArray]);

  const renderOrganizationName = (name) => isDiscreteMode ? 'Confidential' : name;

  // Filter organizations based on the search text
  const filteredOrgs = useMemo(() => {
    return orgArray.filter(org =>
      org.adc_enabled && org.name.toLowerCase().includes(orgSearch.toLowerCase())
    );
  }, [orgArray, orgSearch]);

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error occurred while fetching data</div>;

  let summaryObject, adcRows = [];
  const summaryRows = [];
  Object.values(organizations.entities).forEach((organization) => {
    if (organization.adc_enabled && organization.adc_counts) {
      const orgSummaryObject = organization.adc_counts.find((item) => item.on_date === 'summary');
      if (orgSummaryObject) {
        const sumPercentResolved = orgSummaryObject.visits_resolved_count / orgSummaryObject.visits_count;
        const sumUniquePercentResolved = orgSummaryObject.distinct_visits_resolved_count / orgSummaryObject.distinct_visits_count;
        summaryObject = {
          id: `${organization.id}-summary`,
          ...orgSummaryObject,
          percent_resolved: sumPercentResolved,
          unique_percent_resolved: sumUniquePercentResolved,
          member_name: organization.name,
        };
        summaryRows.push(summaryObject);
      }
      const orgAdcRows = organization.adc_counts.filter((item) => item.on_date !== 'summary').map((item, index) => {
        const percentResolved = item.visits_resolved_count / item.visits_count;
        const uniquePercentResolved = item.distinct_visits_resolved_count / item.distinct_visits_count;
        return {
          id: `${organization.id}-${index}`,
          ...item,
          percent_resolved: percentResolved,
          unique_percent_resolved: uniquePercentResolved,
          member_name: organization.name,
        };
      });
      adcRows = [...adcRows, ...orgAdcRows];
    }
  });

  const averagedDataList = [];
  Object.values(organizations.entities).forEach((organization) => {
    if (organization.adc_enabled && organization.adc_counts) {
      const orgAdcRows = organization.adc_counts
        .filter((item) => item.on_date !== 'summary')
        .map((item, index) => {
          const percentResolved = item.visits_resolved_count / item.visits_count;
          const uniquePercentResolved = item.distinct_visits_resolved_count / item.distinct_visits_count;
          return {
            id: `${organization.id}-${index}`,
            ...item,
            percent_resolved: percentResolved,
            unique_percent_resolved: uniquePercentResolved,
            member_name: organization.name,
          };
        });
    } else {
      console.warn('adc_counts is undefined for organization:', organization);
    }
  });

  return (
    <Box>
      <Header title="ADC Count Summaries" />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl sx={{ m: 1, minWidth: 150 }}>
            <InputLabel id="date-range-label">Date Range</InputLabel>
            <Select
              labelId="date-range-label"
              value={dateRange}
              onChange={handleDateRangeChange}
              label="Date Range"
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="7d">7 Day</MenuItem>
              <MenuItem value="30d">30 Day</MenuItem>
              <MenuItem value="365d">1 Year</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          {dateRange === 'custom' && (
            <Box sx={{ display: 'flex', gap: 2, m: 1 }}>
              <TextField
                label="Start Date"
                type="date"
                value={customRange.start}
                onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={customRange.end}
                onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl sx={{ m: 1, minWidth: 120 }}>
            <InputLabel id="chart-stat-label">Chart Stat</InputLabel>
            <Select
              labelId="chart-stat-label"
              value={selectedStat}
              onChange={handleStatChange}
              label="Chart Stat"
            >
              <MenuItem value="visits_count">Total Visits</MenuItem>
              <MenuItem value="visits_resolved_count">Total Visits Resolved</MenuItem>
              <MenuItem value="distinct_visits_count">Total Distinct Visits</MenuItem>
              <MenuItem value="distinct_visits_resolved_count">Total Distinct Visits Resolved</MenuItem>
              <MenuItem value="distinct_hems_count">Total Distinct HEMs</MenuItem>
              <MenuItem value="percent_resolved">Percent Visits Resolved</MenuItem>
              <MenuItem value="unique_percent_resolved">Percent Distinct Visits Resolved</MenuItem>
            </Select>
          </FormControl>
          {/* Search Bar for Organizations with same width & thicker outline */}
          <TextField
            label="Search Organizations"
            value={orgSearch}
            onChange={(e) => setOrgSearch(e.target.value)}
            sx={{ m: 1, width: 300, "& .MuiOutlinedInput-notchedOutline": { borderWidth: "2px" } }}
          />
          <FormControl sx={{ m: 1, width: 300, overflow: 'auto' }}>
            <InputLabel id="chart-org-select-label">Organizations</InputLabel>
            <Select
              labelId="chart-org-select-label"
              multiple
              value={selectedChartOrgs}
              onChange={handleChartOrgChange}
              input={<OutlinedInput label="Organizations" sx={{ "& .MuiOutlinedInput-notchedOutline": { borderWidth: "2px" } }} />}
              renderValue={(selected) => selected.map(renderOrganizationName).join(', ')}
            >
              {filteredOrgs.map(org => (
                <MenuItem
                  key={org.name}
                  value={org.name}
                  style={{
                    backgroundColor: selectedChartOrgs.includes(org.name) ? 'lightgreen' : 'inherit'
                  }}
                >
                  {renderOrganizationName(org.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={handleSelectAllOrgs} sx={{ ml: 1, borderColor: 'lightgreen', color: 'lightgreen', '&:hover': { borderColor: 'green', color: 'green' } }}>Select All</Button>
            <Button variant="outlined" onClick={handleDeselectAllOrgs} sx={{ ml: 1, borderColor: 'lightgreen', color: 'lightgreen', '&:hover': { borderColor: 'green', color: 'green' } }}>Deselect All</Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Box sx={{ height: 400 }}>
            <LineChart width={700} height={300} data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Legend />
              <Tooltip content={<CustomTooltip />} />
              {selectedChartOrgs.map(org => (
                <Line
                  key={org}
                  type="monotone"
                  dataKey={org}
                  name={renderOrganizationName(org)}
                  stroke={orgColors[org]}
                  activeDot={{ r: 8 }}
                />
              ))}
            </LineChart>
          </Box>
        </Grid>
      </Grid>

      {/* Export Buttons */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button variant="outlined" sx={{ ml: 1, borderColor: 'lightgreen', color: 'lightgreen', '&:hover': { borderColor: 'green', color: 'green' } }} onClick={handleExportCSV}>Export CSV</Button>
        <Button variant="outlined" sx={{ ml: 1, borderColor: 'lightgreen', color: 'lightgreen', '&:hover': { borderColor: 'green', color: 'green' } }} onClick={handleExportJSON}>Export JSON</Button>
      </Box>

      {/* Aggregated Stats Section with Info Icon */}
      <Paper elevation={3} sx={{ p: 2, mb: 2, position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">Overall ADC Stats</Typography>
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoOutlined />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          {/* Left Side: Total Counts */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography>Total Visits: {numberFormat.format(aggregateStats.totalVisits)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>Total Visits Resolved: {numberFormat.format(aggregateStats.totalResolved)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>Total Distinct Visits: {numberFormat.format(aggregateStats.totalDistinctVisits)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>Total Distinct Visits Resolved: {numberFormat.format(aggregateStats.totalDistinctResolved)}</Typography>
              </Grid>
            </Grid>
          </Grid>
          {/* Right Side: Percent Metrics in Gradient Sub-Paper */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                background: `linear-gradient(to right, ${colors.primary[700]}, ${colors.primary[500]})`,
                color: 'white'
              }}
            >
              <Typography>Average % Resolved: {(aggregateStats.avgPercentResolved * 100).toFixed(2)}%</Typography>
              <Typography>Average % Distinct Resolved: {(aggregateStats.avgUniquePercentResolved * 100).toFixed(2)}%</Typography>
              <Typography>Overall Resolution Rate: {(aggregateStats.overallResolutionRate * 100).toFixed(2)}%</Typography>
              <Typography>Overall Distinct Resolution Rate: {(aggregateStats.overallDistinctResolutionRate * 100).toFixed(2)}%</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Aggregated Stats Information</DialogTitle>
        <DialogContent>
          <Typography>
            These stats come from the range specified in the selector above.
            Members with zero total visits are excluded from the aggregated stats calculations,
            since their resolution rates are always zero. The overall resolution rate is calculated
            from the summary numbers (total resolved divided by total visits) rather than as an average of individual rates.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={{ borderColor: colors.redAccent[300], color: colors.redAccent[300] }}
            onClick={() => setInfoOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Typography
        variant="h6"
        color={colors.grey[300]}
        sx={{
          whiteSpace: 'pre-wrap',
          maxWidth: '100%',
          wordWrap: 'break-word',
        }}
      >
        These counts are created by adding the totals from the daily counts on the range specified.
      </Typography>
      <DataGrid
        rows={filteredData}
        columns={columns}
        sx={{ height: 500 }}
        disableColumnMenu
      />
    </Box>
  )
}

export default CountSummaries;
